import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { StatusBadge } from 'components/ui/status-badge';
import { formatGroupedNumber } from 'lib/dateFormat';
import type { Plan } from 'modules/entitlements';
import { planFeatureLine } from './billingHelpers';

interface PlansGridProps {
  plans?: Plan[];
  currentPlanKey: string;
  loading: boolean;
  onCheckout: (plan: Plan) => void;
}

/** Feature lines shown per plan card. */
const FEATURES_PER_CARD = 6;

function PlanCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4" aria-hidden>
      <Skeleton className="h-5 w-24" />
      <Skeleton className="h-6 w-36" />
      <div className="flex flex-col gap-2.5 py-1">
        {[0, 1, 2, 3].map((line) => (
          <Skeleton key={line} className={cn('h-3.5', line % 2 ? 'w-3/4' : 'w-full')} />
        ))}
      </div>
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/** Available-plans catalogue. Only purchasable (priced) plans are listed; the current one is outlined in ink. */
export function PlansGrid({ plans, currentPlanKey, loading, onCheckout }: PlansGridProps) {
  const { t } = useTranslation();
  const purchasable = (plans ?? []).filter((p) => p.monthlyPrice > 0);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[14.5px] font-semibold text-foreground">{t('available_plans', 'Available plans')}</h3>
        <Badge>{t('billed_monthly', 'Billed monthly')}</Badge>
      </div>

      <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy={loading || undefined}>
        {loading
          ? [0, 1, 2].map((i) => <PlanCardSkeleton key={i} />)
          : purchasable.map((plan) => {
              const isCurrent = plan.key === currentPlanKey;
              const isPopular = !isCurrent && plan.key === 'GROWTH';
              const features = plan.features.filter((f) => f.enabled).slice(0, FEATURES_PER_CARD);

              return (
                <article
                  key={plan.key}
                  aria-current={isCurrent || undefined}
                  className={cn(
                    'flex flex-col gap-3 rounded-lg bg-card p-4',
                    isCurrent ? 'border-[1.5px] border-foreground' : 'border border-border',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="truncate text-base font-bold text-foreground">{plan.displayName}</h4>
                    {isCurrent ? (
                      <StatusBadge tone="neutral" dot={false} className="shrink-0">
                        {t('current_plan_2', 'Current plan')}
                      </StatusBadge>
                    ) : (
                      isPopular && <Badge className="shrink-0">{t('popular', 'Popular')}</Badge>
                    )}
                  </div>

                  <p className="text-[13px] text-muted-foreground">
                    <span className="text-xl font-bold tabular-nums text-foreground">
                      {formatGroupedNumber(plan.monthlyPrice)}
                    </span>{' '}
                    {t('billing_som_per_month', "so'm / month")}
                  </p>

                  <ul className="flex flex-col gap-[7px]">
                    {features.map((f) => (
                      <li key={f.featureKey} className="flex items-center gap-2 text-[13px] leading-[18px] text-foreground/80">
                        <Check className="h-3.5 w-3.5 shrink-0 text-success-600" strokeWidth={2.5} aria-hidden />
                        <span className="min-w-0">{planFeatureLine(t, f.featureKey, f.limit)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto">
                    {isCurrent ? (
                      <Button variant="outline" disabled className="w-full">
                        {t('your_current_plan', 'Your current plan')}
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full" onClick={() => onCheckout(plan)}>
                        {t('billing_change_plan', 'Change plan')}
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
      </div>
    </section>
  );
}

export default PlansGrid;
